"use client"

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import debounce from "lodash.debounce"
import { api } from "../../services/Axios"
import {
  FaUserTie,
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
} from "react-icons/fa"
import PageHeader from "../ui/management/PageHeader"
import StatsCard from "../ui/management/StatsCard"
import Pagination from "../ui/management/Pagination"
import Loader from "../utils/loader/Loader"
import ConfirmationModal from "../utils/avisos/ConfirmationModal"
import MessageModal from "../utils/avisos/MessageModal"
import ReusableModal from "../utils/modals/ReusableModal"

export interface Supplier {
  _id: string
  nombre: string
  contacto: string
  telefono: string
  direccion: string
}

function ManageSuppliers() {
  // Estados generales
  const [isReadOnlyMode, setIsReadOnlyMode] = useState<boolean>(false)
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null)
  
  // Estados para el modal de confirmación y mensajes
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalMessage, setModalMessage] = useState<string>("")
  const [modalAction, setModalAction] = useState<(() => void) | null>(null)
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false)
  const [messageContent, setMessageContent] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error">("success")
  
  // Estados para los campos del formulario
  const [nombre, setNombre] = useState<string>("")
  const [contacto, setContacto] = useState<string>("")
  const [telefono, setTelefono] = useState<string>("")
  const [direccion, setDireccion] = useState<string>("")
  const [nombreError, setNombreError] = useState<string>("")
  const [contactoError, setContactoError] = useState<string>("")
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // Paginación
  const suppliersPerPage = 6
  const [totalPages, setTotalPages] = useState<number>(1)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalSuppliers, setTotalSuppliers] = useState<number>(0)

  // Referencia para el input de búsqueda
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Función de búsqueda con debounce para optimizar las llamadas a la API
  const debouncedFetchSuppliers = useMemo(
    () =>
      debounce((page: number, search: string) => {
        setIsLoading(true)
        api
          .get("/api/proveedores", {
            params: {
              page,
              limit: suppliersPerPage,
              searchTerm: search || undefined,
            },
          })
          .then((response) => {
            const { proveedores, totalPages, totalProveedores } = response.data
            setSuppliers(proveedores)
            setTotalPages(totalPages)
            setTotalSuppliers(totalProveedores)
            setCurrentPage(page)
          })
          .catch((error) =>
            console.error("Error al obtener proveedores:", error),
          )
          .finally(() => setIsLoading(false))
      }, 500),
    [suppliersPerPage],
  )

  const fetchSuppliers = useCallback(
    (page: number, search: string) => {
      debouncedFetchSuppliers(page, search)
    },
    [debouncedFetchSuppliers],
  )

  useEffect(() => {
    fetchSuppliers(currentPage, searchTerm)
  }, [fetchSuppliers, currentPage, searchTerm])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    setSearchTerm("")
    setCurrentPage(1)
    if (searchInputRef.current) {
      searchInputRef.current.value = ""
    }
    fetchSuppliers(1, "")
  }

  // Funciones para el modal de confirmación
  const openConfirmationModal = (message: string, action?: () => void) => {
    setModalMessage(message)
    setModalAction(() => action || closeConfirmationModal)
    setModalOpen(true)
  }

  const closeConfirmationModal = () => {
    setModalOpen(false)
    setModalAction(null)
    setModalMessage("")
  }

  // Acciones CRUD
  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier)
    setNombre(supplier.nombre)
    setContacto(supplier.contacto)
    setTelefono(supplier.telefono)
    setDireccion(supplier.direccion)
    setIsEditMode(false)
    setIsReadOnlyMode(true)
    setIsModalOpen(true)
  }

  const handleAddSupplier = () => {
    setIsEditMode(false)
    setNombre("")
    setContacto("")
    setTelefono("")
    setDireccion("")
    setNombreError("")
    setContactoError("")
    setIsModalOpen(true)
  }

  const handleEditSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier)
    setNombre(supplier.nombre)
    setContacto(supplier.contacto)
    setTelefono(supplier.telefono)
    setDireccion(supplier.direccion)
    setNombreError("")
    setContactoError("")
    setIsEditMode(true)
    setIsModalOpen(true)
  }

  const handleSaveSupplier = () => {
    let hasError = false

    if (!nombre.trim()) {
      setNombreError("Este campo es obligatorio.")
      hasError = true
    } else {
      setNombreError("")
    }

    if (!contacto.trim()) {
      setContactoError("Este campo es obligatorio.")
      hasError = true
    } else {
      setContactoError("")
    }

    if (hasError) return

    const saveAction = () => {
      if (isSaving) return; // 🚫 Si ya está guardando, no hace nada
      setIsSaving(true)
      const supplierData = { nombre, contacto, telefono, direccion }
      const action = isEditMode && selectedSupplier
        ? api.put(`/api/proveedores/${selectedSupplier._id}`, supplierData)
        : api.post("/api/proveedores/add", supplierData)

      action
        .then(() => {
          setMessageContent(
            isEditMode
              ? "Proveedor actualizado exitosamente."
              : "Proveedor agregado exitosamente.",
          )
          setMessageType("success")
          setShowMessageModal(true)
          setIsModalOpen(false)
          fetchSuppliers(currentPage, searchTerm)
        })
        .catch((error) => {
          const errorMsg =
            error.response?.data?.msg || "Error al guardar el proveedor."
          setMessageContent(errorMsg)
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => {
          setIsSaving(false)
          closeConfirmationModal()
        })
    }

    openConfirmationModal(
      isEditMode
        ? "¿Está seguro de que desea guardar los cambios en este proveedor?"
        : "¿Está seguro de que desea agregar este proveedor?",
      saveAction,
    )
  }

  const handleDeleteSupplier = (supplierId: string) => {
    openConfirmationModal("¿Está seguro de que desea eliminar este proveedor?", () => {
      api
        .delete(`/api/proveedores/${supplierId}`)
        .then(() => {
          setMessageContent("Proveedor eliminado exitosamente.")
          setMessageType("success")
          setShowMessageModal(true)
          fetchSuppliers(currentPage, searchTerm)
        })
        .catch(() => {
          setMessageContent("Error al eliminar el proveedor.")
          setMessageType("error")
          setShowMessageModal(true)
        })
        .finally(() => setModalOpen(false))
    })
  }

  const handleCloseMessageModal = () => {
    setShowMessageModal(false)
    setModalOpen(false)
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {isLoading && <Loader />}
      <ConfirmationModal
        isOpen={modalOpen}
        title="Confirmación"
        message={modalMessage}
        onConfirm={modalAction || closeConfirmationModal}
        onCancel={closeConfirmationModal}
        isProcessing={isSaving}
      />
      <MessageModal
        isOpen={showMessageModal}
        message={messageContent}
        messageType={messageType}
        onClose={handleCloseMessageModal}
      />

      {/* Encabezado de la página */}
      <PageHeader
        title="Gestión de Proveedores"
        subtitle="Administra tus proveedores"
        icon={FaUserTie}
        actionLabel="Agregar Proveedor"
        onAction={handleAddSupplier}
        actionIcon={FaPlus}
      />

      {/* Sección de filtros y búsqueda */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FaFilter className="mr-2 text-[#2d53da]" /> Filtros de Proveedores
          </h2>
          <button
            onClick={clearFilters}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200"
          >
            <i className="fa-solid fa-circle-xmark mr-2"></i> Borrar Filtros
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por nombre"
            onChange={handleSearchChange}
            className="pl-10 w-full p-3 border border-gray-300 rounded-lg focus:ring-[#2d53da] focus:border-[#2d53da] transition-colors duration-200"
            ref={searchInputRef}
          />
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatsCard
          title="Total Proveedores"
          value={totalSuppliers.toString()}
          description="Proveedores registrados"
          icon={FaUserTie}
          gradient={false}
        />
      </div>

      {/* Lista de proveedores */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        {suppliers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contacto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dirección
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {suppliers.map((supplier) => (
                  <tr key={supplier._id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{supplier.nombre}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{supplier.contacto}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{supplier.direccion}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-100 text-blue-600 hover:bg-blue-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleViewSupplier(supplier)}
                          title="Ver Proveedor"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="bg-yellow-100 text-yellow-600 hover:bg-yellow-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleEditSupplier(supplier)}
                          title="Editar Proveedor"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="bg-red-100 text-red-600 hover:bg-red-200 p-2 rounded-full transition-colors duration-200"
                          onClick={() => handleDeleteSupplier(supplier._id)}
                          title="Eliminar Proveedor"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FaUserTie className="text-gray-400 text-3xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No se encontraron proveedores</h3>
            <p className="text-gray-500 mb-4">
              Intenta con otra búsqueda o agrega un nuevo proveedor.
            </p>
            <button
              onClick={handleAddSupplier}
              className="flex items-center px-4 py-2 bg-[#2d53da] hover:bg-[#2d53da]/90 text-white rounded-lg transition-colors duration-200"
            >
              <FaPlus className="mr-2" /> Agregar Proveedor
            </button>
          </div>
        )}
      </div>

      {/* Paginación */}
      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {/* Modal para agregar/editar o ver proveedor */}
      <ReusableModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setIsReadOnlyMode(false)
        }}
        title={isReadOnlyMode ? "Ver Proveedor" : isEditMode ? "Editar Proveedor" : "Agregar Proveedor"}
        fields={[
          { label: "Nombre", value: nombre, onChange: setNombre, required: true, error: nombreError, readOnly: isReadOnlyMode },
          { label: "Contacto", value: contacto, onChange: setContacto, required: true, error: contactoError, readOnly: isReadOnlyMode },
          { label: "Teléfono", value: telefono, onChange: setTelefono, readOnly: isReadOnlyMode },
          { label: "Dirección", value: direccion, onChange: setDireccion, readOnly: isReadOnlyMode },
        ]}
        onSave={handleSaveSupplier}
        isSaving={isSaving}
        saveButtonText={isEditMode ? "Guardar Cambios" : "Agregar"}
        readOnlyMode={isReadOnlyMode}
      />
    </div>
  )
}

export default ManageSuppliers
